import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import {
  IconArchive,
  IconCircleCheck,
  IconCopy,
  IconEdit,
  IconFileCertificate,
  IconGripVertical,
  IconNewSection,
  IconNotes,
  IconTrash,
  IconUserPlus,
  IconWorld,
} from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import uniq from 'lodash/uniq';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
import {
  clonePlan,
  type ContentStep,
  getActivePlanAssignmentsQueryKey,
  getManagedPlansQueryKey,
  getPlanAndAssignmentsOptions,
  getPlanAndAssignmentsQueryKey,
  patchPlanStatusMutation,
} from '@cfa/pathway-fe-shared/xp';
import { Card, Dropdown } from '@cfa/react-components';
import {
  Button,
  Divider,
  Menu,
  MessageBlock,
  Surface,
  TextInput,
} from '@cfa/react-core';
import {
  FileCertificate,
  NewSection,
  Notes,
  Plus,
  SuccessOutline,
} from '@cfa/system-icons';

import { useUpdatePlan } from '@/hooks/useUpdatePlan';

import { isValidPlanId } from '@/utils/isValidPlanId';
import { createCurrentLanguageValue, itemByLanguage } from '@/utils/language';
import { invalidatePlanArchiveStatusQueries } from '@/utils/queryInvalidation';
import { isApiError } from '@/utils/request';
import { showAssignmentSuccessToast } from '@/utils/training-plans/show-assignment-success-toast';
import { useRouteMatcher } from '@/utils/url';
import { withRoles } from '@/utils/withRoles';

import StickyMenuButton from '@/components/Cards/StickyMenuCard/StickyMenuButton';
import StickyMenuCard from '@/components/Cards/StickyMenuCard/StickyMenuCard';
import InputDialog from '@/components/Dialogs/InputDialog';
import GenericError from '@/components/Error/GenericError';
import { Fab } from '@/components/Fab/Fab';
import FabPopupItem from '@/components/Fab/FabPopupItem';
import { Header } from '@/components/header';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import PopoverMenuButton from '@/components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from '@/components/PopoverMenuButton/PopoverMenuButtonItem';
import AddProcedurePopup from '@/components/popups/AddProcedurePopUp/AddProcedurePopup';
import { PopupDocument } from '@/components/popups/AddProcedurePopUp/types';
import { AssignTeamMembersModal } from '@/components/popups/AssignTeamMembersModal';
import ConfirmationModal from '@/components/popups/ConfirmationModal';
import ManageTranslationsModal from '@/components/popups/ManageTranslationsModal/ManageTranslationsModal';
import type { ManageTranslationsState } from '@/components/popups/ManageTranslationsModal/state';
import QuizLibraryPopUp from '@/components/popups/QuizLibraryPopUp';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants, { routes } from '@/constants';

import { queryClient } from '@/services';
import { CustomError } from '@/types/types';

import { useAddReadOnlyPlan } from '../hooks/useAddReadOnlyPlan/useAddReadOnlyPlan';
import RemovePlan from '../ManagePlans/Tab/Modals/RemovePlan';

import { AddTask } from './EditablePlanItems/AddTask';
import EditButtons from './EditablePlanItems/EditButtons';
import EditPlanDescription from './EditablePlanItems/EditPlanDescription';
import { useEditPlanDraft } from './hooks/useEditPlanDraft';
import { useSavePlanDescription } from './hooks/useSavePlanDescription';
import {
  procedureDraftIsInSectionAtom,
  procedureDraftPlanIdAtom,
  procedureDraftSectionIdAtom,
} from './state';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { userPrefsAtom } from '@/atoms/userPrefs';
import { TranslationKey } from '@/i18n';
import PageNotFound404 from '@/routes/404/404';
import { buildDuplicatePlanName } from '@/routes/TrainingPlans/buildDuplicatePlanName';
import PlanItems from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/PlanItems';
import { useAssignTeamMembers } from '@/routes/TrainingPlans/hooks/useAssignTeamMembers';
import useIsValidPlanName from '@/routes/TrainingPlans/hooks/useIsValidPlanName';
import { hasActiveAssignees } from '@/routes/TrainingPlans/planBuildTimer';

const EditPlanView = () => {
  const {
    addSection: addSectionToplan,
    updateName: renamePlan,
    replaceStepResource,
    updateCategory,
    updateDescription,
    updateEstimatedTime,
    updateTranslations,
    isPending: isPlanUpdatePending,
  } = useUpdatePlan();
  const {
    buildQuizzes: featureFlagBuildQuizzes,
    planDescriptions: featureFlagPlanDescriptions,
  } = useFlags();
  const routeMatches = useRouteMatcher();
  const { planId } = useParams<{ planId: string }>();
  const safePlanId = planId ?? '';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const track = useAmplitudeTrack();
  const { assignTeamMembers } = useAssignTeamMembers();

  const trainingBuildPathname = routes.plans.managedPlans;

  const { language: userLanguage } = useAtomValue(userPrefsAtom);

  const [showAddSection, setShowAddSection] = useState(false);
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [showAddProcedure, setShowAddProcedure] = useState(false);
  const [showAddQuiz, setShowAddQuiz] = useState(false);
  const [isInSection, setIsInSection] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showRemovePlan, setShowRemovePlan] = useState(false);
  const [showArchivePlanPopup, setShowArchivePlanPopup] = useState(false);
  const [showAddPlanItemsMenu, setShowAddPlanItemsMenu] = useState(false);
  const [showRenamePlanPopup, setShowRenamePlanPopup] = useState(false);
  const [showAddTeamMembersPopup, setShowAddTeamMembersPopup] = useState(false);

  const [showDuplicatePlanPopup, setShowDuplicatePlanPopup] = useState(false);

  const [showManageTranslationsPopup, setShowManageTranslationsPopup] =
    useState(false);
  const [showInvalidNameError, setShowInvalidNameError] = useState(false);
  const [sectionId, setSectionId] = useState('');
  const [selectedStepId, setSelectedStepId] = useState('');
  const [addSectionText, setAddSectionText] = useState('');
  const [isDrop, setIsDrop] = useState(false);

  const [estimatedMinutes, setEstimatedMinutes] = useState('');
  const [pendingDescription, setPendingDescription] = useState<string | null>(
    null,
  );
  const [onlyAllowOneDoc, setOnlyAllowOneDoc] = useState(false);
  const [taskValues, setTaskValues] = useState({
    task: '',
    notes: '',
    link: '',
  });

  const refContainerEnd = useRef<HTMLDivElement>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const { mutate: archivePlan } = useMutation({
    ...patchPlanStatusMutation(),
    onSuccess: () => {
      track(AmplitudeProductEvent.ARCHIVE_PLAN_CLICKED, {
        plan_id: plan?.id ?? safePlanId,
        plan_type: plan?.type,
      });
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${planName} ${t('TrainingPlans.toastMessage.archived')}`}
        </ToastMessageBlock>
      ));
    },
    onSettled: async () => {
      await invalidatePlanArchiveStatusQueries();
      setShowArchivePlanPopup(false);
      navigate({ pathname: trainingBuildPathname });
    },
    onError: error => {
      datadogRum.addError(
        error instanceof Error ? error : new Error(JSON.stringify(error)),
      );
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {t('TrainingPlans.toastMessage.errorArchiving')}
        </ToastMessageBlock>
      ));
    },
  });

  const isTrainingPlanPath = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.TrainingTeamPath,
  );

  const {
    data: planAndAssignments,
    isLoading: isFetchingPlan,
    isPending,
    error,
    refetch: _refetch,
  } = useQuery({
    ...getPlanAndAssignmentsOptions({ path: { planId: safePlanId } }),
    enabled: !!safePlanId && isValidPlanId(safePlanId),
  });

  const refetch = async () => {
    if (!isValidPlanId(safePlanId)) {
      throw new Error('Invalid planId');
    }
    return _refetch();
  };

  const plan = planAndAssignments?.plan;

  const isReadOnly = plan?.readonly;
  const isCompliance = plan?.type === 'COMPLIANCE' || plan?.type === 'ANCHOR';

  const savedPlanDescription = itemByLanguage(plan?.description, '');
  const descriptionValue = pendingDescription ?? savedPlanDescription;

  const planDescriptionSave = useSavePlanDescription({
    plan,
    isReadOnly,
    userLanguage,
    updateDescription,
    isPlanUpdatePending,
    descriptionValue,
    savedPlanDescription,
    onDescriptionSaved: ({ wasCleared, action }) => {
      setPendingDescription(null);
      track(AmplitudeProductEvent.PLAN_DESCRIPTION_SAVED, {
        training_plan_id: plan?.id,
        description_action: action,
      });
      const descriptionToastSuffix = wasCleared
        ? t('TrainingPlans.planDescription.toastRemoved')
        : t('TrainingPlans.toastMessage.updated');
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${itemByLanguage(plan?.name, '')} ${descriptionToastSuffix}`}
        </ToastMessageBlock>
      ));
    },
  });

  const refetchPlanLists = async () => {
    await Promise.all([
      queryClient.refetchQueries({
        queryKey: getManagedPlansQueryKey({ query: { status: 'ACTIVE' } }),
      }),
      // In case you assign a Plan to yourself, we do a refetch.
      queryClient.refetchQueries({
        queryKey: getActivePlanAssignmentsQueryKey(),
      }),
      queryClient.refetchQueries({
        queryKey: getPlanAndAssignmentsQueryKey({
          path: { planId: safePlanId },
        }),
      }),
    ]);
  };

  const { unsubscribeReadonlyPlan, isPending: isRemovePending } =
    useAddReadOnlyPlan();

  const handleRemove = async () => {
    if (!plan) {
      return;
    }
    await unsubscribeReadonlyPlan(plan);
    setShowRemovePlan(false);
    navigate(routes.plans.managedPlans);
  };

  const [prevPlan, setPrevPlan] = useState(plan);
  if (prevPlan !== plan) {
    setPrevPlan(plan);
    if (plan) {
      setEstimatedMinutes(
        plan.estimatedTimeToCompleteInMinutes?.toString() ?? '',
      );
      setPendingDescription(null);
    }
  }

  useEffect(() => {
    if ((showAddSection || showAddTask) && refContainerEnd.current) {
      refContainerEnd.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showAddSection, showAddTask, refContainerEnd]);

  // Restore procedure modal if there's a draft matching this plan
  const draftPlanId = useAtomValue(procedureDraftPlanIdAtom);
  const draftSectionId = useAtomValue(procedureDraftSectionIdAtom);
  const draftIsInSection = useAtomValue(procedureDraftIsInSectionAtom);

  const restoreProcedureDraft = useEffectEvent(() => {
    if (draftPlanId === safePlanId) {
      setShowAddProcedure(true);
      setSectionId(draftSectionId);
      setIsInSection(draftIsInSection);
    }
  });

  useEffect(() => {
    if (!safePlanId) return;
    restoreProcedureDraft();
  }, [safePlanId]);

  // Save/restore edit plan draft (quiz/task/section forms, estimated minutes, description)
  useEditPlanDraft({
    planId: safePlanId,
    planEstimatedMinutes:
      plan?.estimatedTimeToCompleteInMinutes?.toString() ?? '',
    planDescription: savedPlanDescription,
    state: {
      showAddQuiz,
      quizSectionId: sectionId,
      quizIsInSection: isInSection,
      showAddTask,
      taskValues,
      showAddSection,
      addSectionText,
      estimatedMinutes,
      description: descriptionValue,
    },
    onRestore: restored => {
      if (restored.showAddQuiz) {
        setShowAddQuiz(true);
        setSectionId(restored.quizSectionId ?? '');
        setIsInSection(restored.quizIsInSection ?? false);
      }
      if (restored.showAddTask) {
        setShowAddTask(true);
        if (restored.taskValues) {
          setTaskValues(restored.taskValues);
        }
      }
      if (restored.showAddSection) {
        setShowAddSection(true);
        setAddSectionText(restored.addSectionText ?? '');
      }
      if (restored.estimatedMinutes) {
        setEstimatedMinutes(restored.estimatedMinutes);
      }
      if (restored.description) {
        setPendingDescription(restored.description);
      }
    },
  });

  const onSaveEstimatedMinutes = async () => {
    if (plan) {
      await updateEstimatedTime(plan, parseInt(estimatedMinutes));
      refetch();
    }
  };

  const planName = itemByLanguage(plan?.name, '');
  const duplicatePlanDefaultName = buildDuplicatePlanName(
    planName,
    t('TrainingPlans.duplicatePlanInputCopy'),
  );

  const isValidPlanName = useIsValidPlanName({
    onlyCheckName: true,
    onInvalidName: () => setShowInvalidNameError(true),
  });

  const onRenamePlan = async (newName: string) => {
    const trimmedName = newName.trim();
    if (!isValidPlanName(trimmedName, plan?.ownerId)) {
      return;
    }
    setShowRenamePlanPopup(false);
    const name = { ...plan?.name, [userLanguage]: trimmedName };
    if (!plan) return;
    await renamePlan(plan, name);
    refetch();
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {`${trimmedName} ${t('TrainingPlans.toastMessage.renamed')}`}
      </ToastMessageBlock>
    ));
  };

  const onSaveTranslations = async (
    updatedTranslations: ManageTranslationsState,
  ) => {
    setShowManageTranslationsPopup(false);
    if (!plan) return;
    await updateTranslations(plan, {
      name: updatedTranslations.name,
      description: updatedTranslations.description,
      sections: updatedTranslations.content?.sections,
      steps: updatedTranslations.content?.steps,
    });
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {`${itemByLanguage(
          updatedTranslations.name,
          '',
        )} ${t('TrainingPlans.translationToastText')} ${
          userLanguage === Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE
            ? Constants.LANGUAGE_OPTIONS.SPANISH
            : Constants.LANGUAGE_OPTIONS.INGLES
        }`}
      </ToastMessageBlock>
    ));
    refetch();
  };

  const onArchivePlan = () => {
    setIsArchiving(true);
    archivePlan({
      path: { planId: safePlanId },
      body: { status: 'INACTIVE' },
    });
  };

  const onDuplicatePlan = async (name: string) => {
    const ownerId = plan?.ownerId;
    const trimmedName = name
      .trim()
      .slice(0, Constants.FORMS.MAX_PLAN_NAME_LENGTH);
    if (!isValidPlanName(trimmedName, ownerId) || !ownerId) {
      return;
    }

    const { error } = await clonePlan({
      path: { planId: safePlanId },
      body: {
        name: createCurrentLanguageValue(trimmedName, userLanguage),
        ownerId,
      },
    });

    if (error) {
      datadogRum.addError(
        error instanceof Error
          ? error
          : new Error('Clone plan request failed', { cause: error }),
      );
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {t('TrainingPlans.toastMessage.errorCopyingPlan')}
        </ToastMessageBlock>
      ));
      return;
    }

    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {`${trimmedName} ${t('TrainingPlans.toastMessage.duplicated')}`}
      </ToastMessageBlock>
    ));
    await refetchPlanLists();
    navigate(
      {
        pathname: trainingBuildPathname,
      },
      {
        state: { duplicatedPlan: trimmedName },
      },
    );
  };

  const changeCategory = async (category: string) => {
    if (!plan) return;
    await updateCategory(plan, category);
    refetch();
  };

  const addSection = async () => {
    if (isAddingSection) {
      return;
    }

    const sectionName = addSectionText.trim();
    if (!sectionName.length) {
      return;
    }

    const newSection = {
      id: uuidv4(),
      name: createCurrentLanguageValue(sectionName, userLanguage),
      steps: [] as ContentStep[],
    };

    if (!plan) return;

    // Close the inline add form immediately to avoid a transient duplicate
    // appearance while the mutation response updates section list data.
    setAddSectionText('');
    setShowRenamePlanPopup(false);
    setShowAddSection(false);
    setIsAddingSection(true);

    try {
      await addSectionToplan(plan, newSection);
    } catch {
      // Restore the draft text if add fails so the user can retry.
      setAddSectionText(sectionName);
      setShowAddSection(true);
    } finally {
      setIsAddingSection(false);
    }
  };

  const onAssignTeamMembers = (
    members: string | string[],
    dueDate: string | null,
  ) => {
    const payload = Array.isArray(members)
      ? { userIds: uniq(members), dueDate: dueDate ?? undefined }
      : { userIds: [members], dueDate: dueDate ?? undefined };

    const numberOfTeamMembersToAssign = payload.userIds.length;
    const isFirstAssignment = !hasActiveAssignees(
      planAndAssignments?.assignments,
    );

    assignTeamMembers({
      planId: safePlanId,
      userIds: payload.userIds,
      dueDate: payload.dueDate,
      isFirstAssignment,
    })
      .then(async () => {
        await refetchPlanLists();
        navigate(
          {
            pathname:
              plan?.id && routes.plans.managePlan({ planId: safePlanId }),
          },
          {
            state: { refetch: true },
          },
        );
        showAssignmentSuccessToast(numberOfTeamMembersToAssign, t);
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
    setShowAddTeamMembersPopup(false);
  };
  const updateStepResource = async (selectedDocuments: PopupDocument[]) => {
    const selectedDoc = selectedDocuments[0];
    if (!selectedDoc) {
      datadogRum.addError(
        new Error('updateStepResource called with empty document selection'),
      );
      return;
    }

    if (!plan) return;
    await replaceStepResource(
      plan,
      selectedStepId,
      { name: selectedDoc.name, reference: selectedDoc.reference.id },
      sectionId || undefined,
    );
    setOnlyAllowOneDoc(false);
    setSectionId('');
    setSelectedStepId('');
    setShowAddProcedure(false);
  };

  const onShowDuplicatePlanPopup = () => {
    setShowDuplicatePlanPopup(true);
  };

  const onShowRenamePlanPopup = () => {
    setShowRenamePlanPopup(true);
  };

  const onRenamePlanCancel = () => {
    setShowRenamePlanPopup(false);
  };

  const onShowArchivePlanPopup = () => {
    setShowArchivePlanPopup(true);
  };

  const onArchivePlanCancel = () => {
    setShowArchivePlanPopup(false);
  };

  const onShowAddTeamMembersPopup = () => {
    setShowAddTeamMembersPopup(true);
  };

  const onShowManageTranslationsPopup = () => {
    setShowManageTranslationsPopup(true);
  };

  const onShowAddTask = () => {
    setShowAddTask(true);
    setShowAddPlanItemsMenu(false);
  };

  const onShowAddQuizToPlanOutsideSection = () => {
    setSectionId('');
    setShowAddQuiz(true);
    setShowAddPlanItemsMenu(false);
  };

  const onShowAddSection = () => {
    if (isAddingSection) {
      return;
    }
    setShowAddSection(true);
    setShowAddPlanItemsMenu(false);
  };

  const onShowAddProcedure = () => {
    setShowAddProcedure(true);
    setShowAddPlanItemsMenu(false);
  };

  const onCloseProcedurePopUp = () => {
    setIsInSection(false);
    setShowAddProcedure(false);
  };

  const onAddProcedureToSection = (id?: string) => {
    setIsInSection(true);
    if (id) {
      setSectionId(id);
    }
    setShowAddProcedure(true);
  };

  const onAddQuizToSection = (id: string) => {
    setIsInSection(true);
    setSectionId(id);
    setShowAddQuiz(true);
  };

  const onCancelSection = () => {
    if (isAddingSection) {
      return;
    }
    setAddSectionText('');
    setShowAddSection(false);
  };

  const onEditProcedureResource = ({
    stepId,
    sectionId,
  }: {
    stepId: string;
    sectionId?: string;
  }) => {
    setSelectedStepId(stepId);
    if (sectionId) {
      setSectionId(sectionId);
    }
    setOnlyAllowOneDoc(true);
    setShowAddProcedure(true);
  };

  const onCloseQuizPopUp = () => {
    setIsInSection(false);
    setShowAddQuiz(false);
  };

  if (isApiError(error) && !isArchiving) {
    if ((error as CustomError).status === 404) {
      datadogRum.addError(error);
      return <PageNotFound404 />;
    } else {
      datadogRum.addError(error);
      return <GenericError />;
    }
  }

  const actions = [
    {
      name: t('TrainingPlans.manageThreeDotMenu.rename'),
      icon: <IconEdit />,
      onClick: onShowRenamePlanPopup,
      testId: 'ThreeDotMenuPlanPopupRename',
      type: ['plan'],
    },
    {
      name: t('Generic.assign'),
      icon: <IconUserPlus />,
      onClick: onShowAddTeamMembersPopup,
      testId: 'ThreeDotMenuAssignPopup',
      type: ['plan', 'readonly'],
    },
    {
      name: t('Generic.editTranslations'),
      icon: <IconWorld />,
      onClick: onShowManageTranslationsPopup,
      testId: 'ThreeDotMenuManageTranslationsPopup',
      type: ['plan'],
    },
    {
      name: t('TrainingPlans.manageThreeDotMenu.duplicate'),
      icon: <IconCopy />,
      onClick: onShowDuplicatePlanPopup,
      testId: 'duplicatePlanMenuItem',
      type: ['plan'],
    },
    {
      name: t('TrainingPlans.manageThreeDotMenu.archive'),
      icon: <IconArchive />,
      onClick: onShowArchivePlanPopup,
      testId: 'archivePlanMenuItem',
      isDestructive: true,
      type: ['plan'],
    },
    {
      name: t('Settings.removePlanModal.title'),
      icon: <IconTrash />,
      onClick: () => setShowRemovePlan(true),
      testId: 'removePlanMenuItem',
      isDestructive: true,
      type: ['readonly'],
    },
  ];

  const filterActions = (action: (typeof actions)[number]) => {
    if (isReadOnly) {
      return action.type.includes('readonly');
    }
    return action.type.includes('plan');
  };

  const renderAddSection = () => (
    <>
      <TextInput
        aria-label={t('TrainingPlans.searchbarPlaceholder')}
        data-testid="AddSectionFormField"
        className="w-full!"
        maxLength={Constants.FORMS.MAX_SECTION_LENGTH}
        onChange={setAddSectionText}
        onKeyDown={e =>
          e.key === 'Enter' &&
          !isAddingSection &&
          addSectionText.trim().length > 0 &&
          addSection()
        }
        placeholder={t('TrainingPlans.searchbarPlaceholder')}
        value={addSectionText}
      />
      {addSectionText.length > 0 && (
        <div className="b-[-20px] r-[35px] absolute flex">
          <div className="text-grayscale-gray6 flex items-end justify-end text-xs font-normal italic">{`${addSectionText.length}/${Constants.FORMS.MAX_SECTION_LENGTH}`}</div>
        </div>
      )}
    </>
  );

  const renderAddTask = () => (
    <Surface data-testid="StyledCardBody" className="pathway-surface">
      <AddTask
        initialValues={taskValues}
        isInSection={false}
        onValuesChange={setTaskValues}
        plan={plan}
        sectionId={sectionId}
        setShowAddTask={setShowAddTask}
      />
    </Surface>
  );

  return (
    <>
      <Header.Root>
        {plan?.id && (
          <Header.Back to={routes.plans.managePlan({ planId: safePlanId })}>
            {planName}
          </Header.Back>
        )}
        {!isPending && (
          <Header.Title
            tx={isReadOnly ? 'Generic.viewPlan' : 'Generic.editPlan'}
          />
        )}
      </Header.Root>
      <LoadingOverlay isOpen={isFetchingPlan && !isDrop} />
      <div className="md:mt-header-height-with-content-spacing centered-content px-4 max-md:mt-6">
        {isReadOnly && (
          <MessageBlock.Root
            className="mb-8"
            data-testid="EditPlanViewInfoMessage"
            variant="info"
          >
            {t('Settings.planNoModify')}
          </MessageBlock.Root>
        )}
        <h4 className="text-variant-h4 max-md:mb-4">{planName}</h4>
        <div className="flex gap-6">
          <div className="flex w-full flex-col gap-6">
            <LoadingOverlay isOpen={(isFetchingPlan && !isDrop) || isPending} />
            <div className="block md:hidden">
              <PopoverMenuButton
                placement="bottom"
                text={t('TrainingPlans.managePlan')}
              >
                {actions.filter(filterActions).map(action => (
                  <PopoverMenuButtonItem
                    key={`mobile-${action.testId}`}
                    dataTestId={action.testId}
                    icon={action.icon}
                    isDestructive={action.isDestructive}
                    onClick={action.onClick}
                    text={action.name}
                  />
                ))}
              </PopoverMenuButton>
            </div>
            {plan &&
              (!!plan.content?.sections?.length ||
                !!plan.content?.steps?.length) && (
                <PlanItems
                  addProcedure={onAddProcedureToSection}
                  addQuiz={(id: string) => onAddQuizToSection(id)}
                  plan={plan}
                  setIsDrop={setIsDrop}
                  editProcedureResource={onEditProcedureResource}
                  isReadOnly={isReadOnly}
                />
              )}
            {showAddSection && (
              <Card className="relative mb-5 p-4 pt-0!">
                <div className="mt-2 flex w-full">
                  <IconGripVertical className="text-grayscale-gray3! mr-2 translate-x-1! translate-y-2!" />
                  <div className="flex w-full flex-col gap-y-5">
                    {renderAddSection()}
                    <EditButtons
                      onCancel={onCancelSection}
                      onEdit={addSection}
                      primaryButtonText={t('Button.add')}
                      validated={
                        !isAddingSection && addSectionText.trim().length > 0
                      }
                    />
                  </div>
                </div>
              </Card>
            )}

            {showAddTask && <div className="mb-5">{renderAddTask()}</div>}

            {plan && (
              <div className="flex flex-col gap-6">
                {!isReadOnly && (
                  <div className="hidden md:block">
                    <Menu.Root>
                      <Button
                        data-testid="AddPlanItemsMenu"
                        variant="outlined"
                        size="lg"
                        color="secondary"
                        className="max-w-fit"
                      >
                        <Plus size="xs" />
                        Add Content
                      </Button>

                      <Menu.Popover>
                        <Menu.Container className="[&>div]:text-secondary-base-alt!">
                          <Menu.Item
                            data-testid="AddResource"
                            onAction={onShowAddProcedure}
                          >
                            <Notes />
                            {t('Generic.resource')}
                          </Menu.Item>
                          <Menu.Item
                            data-testid="AddTask"
                            onAction={onShowAddTask}
                          >
                            <SuccessOutline />
                            {t('Generic.task')}
                          </Menu.Item>
                          {featureFlagBuildQuizzes && (
                            <Menu.Item
                              data-testid="AddQuiz"
                              onAction={onShowAddQuizToPlanOutsideSection}
                            >
                              <FileCertificate />
                              {t('Generic.quiz')}
                            </Menu.Item>
                          )}
                          <Menu.Item
                            data-testid="AddSection"
                            onAction={onShowAddSection}
                          >
                            <NewSection />
                            {t('Generic.section')}
                          </Menu.Item>
                        </Menu.Container>
                      </Menu.Popover>
                    </Menu.Root>
                  </div>
                )}

                {!isReadOnly && <Divider />}

                <Dropdown
                  disabled={isReadOnly}
                  getOptionId={option => (option as { id: string }).id}
                  getOptionText={option =>
                    t((option as { alias: TranslationKey }).alias)
                  }
                  label={t('TrainingPlans.selectCategory')}
                  onChange={newVal =>
                    changeCategory(
                      newVal?.id ?? Constants.PLAN_CATEGORIES.GENERAL,
                    )
                  }
                  options={
                    isReadOnly
                      ? Constants.ALL_PLAN_CATEGORY_OBJECTS
                      : Constants.ALL_PLAN_CATEGORY_OBJECTS.filter(
                          item =>
                            item.id !== Constants.PLAN_CATEGORIES.COMPLIANCE,
                        )
                  }
                  renderOption={option =>
                    t(
                      Constants.PLAN_CATEGORY_ALIAS_TRANSLATIONS[
                        (option?.key ??
                          Constants.PLAN_CATEGORIES
                            .GENERAL) as keyof typeof Constants.PLAN_CATEGORY_ALIAS_TRANSLATIONS
                      ],
                    )
                  }
                  value={
                    /** Once the BE no longer needs us to support both default and
                     * general categories, then we can remove this code block.
                     */
                    Constants.ALL_PLAN_CATEGORY_OBJECTS.find(categoryObj => {
                      if (plan.category === 'default') {
                        return categoryObj.id === 'general';
                      } else {
                        return categoryObj.id === plan.category;
                      }
                    }) ?? Constants.ALL_PLAN_CATEGORY_OBJECTS[0]
                  }
                />
                {featureFlagPlanDescriptions && (
                  <EditPlanDescription
                    isClearing={planDescriptionSave.isClearing}
                    isReadOnly={isReadOnly}
                    isSaveDisabled={planDescriptionSave.isSaveDisabled}
                    onChange={setPendingDescription}
                    onSave={planDescriptionSave.onSave}
                    value={descriptionValue}
                  />
                )}
                <div className="flex items-center gap-2" ref={refContainerEnd}>
                  <TextInput
                    isDisabled={isReadOnly}
                    data-testid="EstimatedTimeToComplete"
                    description={t(
                      'TrainingPlans.accountability.timeEstimationTooltip',
                    )}
                    label={t(
                      'TrainingPlans.accountability.estimatedTimeToComplete',
                    )}
                    onChange={setEstimatedMinutes}
                    placeholder={
                      isReadOnly
                        ? ''
                        : t('TrainingPlans.accountability.enterTime')
                    }
                    value={estimatedMinutes}
                  />
                  {!isReadOnly && (
                    <Button
                      className="mt-2"
                      color="secondary"
                      data-testid="SaveEstimatedTime"
                      isDisabled={
                        estimatedMinutes.length <= 0 ||
                        estimatedMinutes ===
                          String(plan.estimatedTimeToCompleteInMinutes)
                      }
                      onPressEnd={onSaveEstimatedMinutes}
                    >
                      {t('Button.save')}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="hidden shrink-0 md:block">
            <StickyMenuCard
              header={t('TrainingPlans.managePlan')}
              data-testid="StickyMenuCard"
              className="md:top-[calc(var(--header-height)+16px)]!"
            >
              {actions.filter(filterActions).map(action => (
                <StickyMenuButton
                  key={`desktop-${action.testId}`}
                  dataTestId={action.testId}
                  icon={action.icon}
                  error={action.isDestructive}
                  onClick={action.onClick}
                  text={action.name}
                />
              ))}
            </StickyMenuCard>
          </div>
        </div>
      </div>
      <Fab
        isOpen={showAddPlanItemsMenu}
        onClick={() => setShowAddPlanItemsMenu(!showAddPlanItemsMenu)}
        onClose={() => setShowAddPlanItemsMenu(false)}
        data-testid="AddPlanItemsMenu"
        className="block md:hidden"
        icon="add-content"
      >
        <FabPopupItem
          data-testid="AddResource"
          icon={<IconNotes />}
          onClick={onShowAddProcedure}
          title={t('Generic.resource')}
        />
        <FabPopupItem
          data-testid="AddTask"
          icon={<IconCircleCheck />}
          onClick={onShowAddTask}
          title={t('Generic.task')}
        />
        {featureFlagBuildQuizzes && (
          <FabPopupItem
            data-testid="AddQuiz"
            icon={<IconFileCertificate />}
            onClick={onShowAddQuizToPlanOutsideSection}
            title={t('Generic.quiz')}
          />
        )}
        <FabPopupItem
          data-testid="AddSection"
          icon={<IconNewSection />}
          onClick={onShowAddSection}
          title={t('Generic.section')}
        />
      </Fab>
      <ConfirmationModal
        bodyText={t('InvalidPlanName.paragraphText')}
        headerText={t('InvalidPlanName.errorHeader')}
        isError={true}
        isOpen={showInvalidNameError}
        onClose={() => setShowInvalidNameError(false)}
        primaryButtonHandler={() => setShowInvalidNameError(false)}
        primaryButtonText={t('Button.close')}
      />
      {isTrainingPlanPath && (
        <>
          <ConfirmationModal
            bodyText={t('Generic.archiveObject')}
            headerText={t('Generic.archiveHeader', {
              type: t('Generic.plan'),
            })}
            isLoading={isArchiving}
            isOpen={showArchivePlanPopup}
            onClose={() => setShowArchivePlanPopup(false)}
            primaryButtonHandler={onArchivePlan}
            primaryButtonText={t('Button.archive')}
            secondaryButtonHandler={onArchivePlanCancel}
            secondaryButtonText={t('Button.cancel')}
            primaryButtonVariant="destructive"
            stepKey="archive-plan"
          />
          <InputDialog
            headerText={t('Generic.giveNewName', {
              object: t('Generic.plan'),
            })}
            isOpen={showRenamePlanPopup}
            onClose={onRenamePlanCancel}
            primaryButtonHandler={onRenamePlan}
            primaryButtonText={t('Button.update')}
            primaryButtonVariant="filled"
            secondaryButtonHandler={onRenamePlanCancel}
            secondaryButtonText={t('Button.cancel')}
            textInputDefaultValue={planName}
            textInputMaxLength={Constants.FORMS.MAX_PLAN_NAME_LENGTH}
            textInputPlaceholder={t('TrainingPlans.planNamePlaceholder')}
            keyDownEnter
          />
          <AssignTeamMembersModal
            isOpen={showAddTeamMembersPopup}
            onClose={() => setShowAddTeamMembersPopup(false)}
            plan={plan}
            isCompliance={isCompliance}
            reassignEligible
            teamMemberQueryOptions={{ refetchOnMount: true }}
            onAssign={onAssignTeamMembers}
          />
          <AddProcedurePopup
            isInSection={isInSection}
            isOpen={showAddProcedure}
            onClose={onCloseProcedurePopUp}
            plan={plan}
            sectionId={sectionId}
            onCustomAddProcedure={
              onlyAllowOneDoc ? updateStepResource : undefined
            }
            onlyAllowOneDoc={onlyAllowOneDoc}
            textVariant={onlyAllowOneDoc ? 'update' : 'default'}
          />
          <QuizLibraryPopUp
            isOpen={showAddQuiz}
            onClose={onCloseQuizPopUp}
            plan={plan}
            sectionId={sectionId}
          />
          <InputDialog
            headerText={t('Generic.giveName', {
              object: t('Generic.plan'),
            })}
            allowBlankInput={true}
            isOpen={showDuplicatePlanPopup}
            onClose={() => setShowDuplicatePlanPopup(false)}
            primaryButtonHandler={onDuplicatePlan}
            primaryButtonText={t('Button.duplicate')}
            primaryButtonVariant="filled"
            secondaryButtonHandler={() => setShowDuplicatePlanPopup(false)}
            secondaryButtonText={t('Button.cancel')}
            textInputDefaultValue={duplicatePlanDefaultName}
            textInputMaxLength={Constants.FORMS.MAX_PLAN_NAME_LENGTH}
            textInputPlaceholder={t('TrainingPlans.planNamePlaceholder')}
          />
          <ManageTranslationsModal
            isOpen={showManageTranslationsPopup}
            onClose={() => setShowManageTranslationsPopup(false)}
            onSave={onSaveTranslations}
            translatableObject={plan}
          />
          {plan && (
            <RemovePlan
              isLoading={isRemovePending}
              onRemove={handleRemove}
              plan={plan}
              setShowRemovePlan={setShowRemovePlan}
              showRemovePlan={showRemovePlan}
            />
          )}
        </>
      )}
    </>
  );
};

const EditPlanViewWithRoles = withRoles(EditPlanView, 'plans.create');

export default EditPlanViewWithRoles;
